<template>
  <div class="item">
    <div>
      <v-avatar
        :size="avatarSize"
        class="sn-background-brand-02"
        :style="`${pointerCursor ? 'cursor: pointer;': ''}`"
        v-on="on"
        @click="$emit('click')"
      >
        <div
          v-if="showOverlay"
          :style="`height: ${overlayHeight}px`"
          :class="`${!!validImage ? 'avatar-overlay' : 'initials-overlay'}`"
        >
          <v-icon
            color="white"
            :size="overlayHeight"
            :style="cssProps"
            class="overlay-icon"
          >
            $snMenuDotsHorizontalSolid
          </v-icon>
        </div>
        <img
          v-if="validImage"
          :src="validImage"
          alt=""
          @error="handleImageError"
        >
        <div
          v-else
          class="d-flex align-center justify-center"
        >
          <v-icon
            v-if="icon"
            :size="iconSize"
            color="snBrand03"
          >
            {{ icon }}
          </v-icon>
          <span
            v-if="initials"
            :class="[initialsTextClass, 'sn-text-brand-03']"
          >
            {{ initials }}
          </span>
        </div>
      </v-avatar>
      <div
        v-if="showImageUploader"
        class="notify-badge"
        :style="styles"
      >
        <v-icon
          class="icon-color"
          :size="getCameraSize"
          @click="handleCameraClick"
        >
          sn-icon-camera
        </v-icon>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SnAvatar',
  props: {
    initials: {
      type: String,
      default: '',
      required: false
    },
    image: {
      type: String,
      required: false,
      default: ''
    },
    showOverlay: {
      type: Boolean,
      required: false,
      default: false
    },
    pointerCursor: {
      type: Boolean,
      required: false,
      default: false
    },
    icon: {
      type: String,
      default: '',
      required: false
    },
    size: {
      type: String,
      default: 'xs',
      required: false
    },
    on: {
      type: Object,
      default: () => ({})
    },
    imageUploader: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      imageError: false
    }
  },
  computed: {
    overlayHeight () {
      return this.avatarSize * 0.28125
    },
    validImage () {
      if (this.imageError) return ''
      return this.image
    },
    cssProps () {
      if (this.image) {
        return {
          '--fill-color': 'white'
        }
      }
      return {
        '--fill-color': this.$vuetify.theme?.themes?.light?.primary ?? this.$vuetify.theme.primary
      }
    },
    avatarSize () {
      switch (this.size) {
        case 'xxxs':
          return '18'
        case 'xxs':
          return '32'
        case 'xs':
          return '40'
        case 'sm':
          return '48'
        case 'md':
          return '64'
        case 'lg':
          return '80'
        case 'xl':
          return '104'
        case 'xxl':
          return '160'
        case 'xxxl':
          return '200'
        default:
          return '40'
      }
    },
    initialsTextClass () {
      switch (this.size) {
        case 'xxxs':
          return 'xxxs-font'
        case 'xxs':
          return 'sn-caption'
        case 'xs':
          return 'sn-subtitle-1'
        case 'sm':
          return 'sn-h4'
        case 'md':
          return 'sn-h2'
        case 'lg':
          return 'sn-h2'
        case 'xl':
          return 'sn-h1'
        case 'xxl':
          return 'sn-display-2'
        case 'xxxl':
          return 'sn-display-1'
        default:
          return 'sn-h2'
      }
    },
    iconSize () {
      switch (this.size) {
        case 'xxxs':
          return '12'
        case 'xxs':
          return '16'
        case 'xs':
          return '20'
        case 'sm':
          return '24'
        case 'md':
          return '36'
        case 'lg':
          return '36'
        case 'xl':
          return '36'
        case 'xxl':
          return '36'
        case 'xxxl':
          return '36'
        default:
          return '36'
      }
    },
    showImageUploader () {
      let validSize = false
      if (this.size) {
        validSize = ['sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'].includes(this.size)
      }
      return this.imageUploader && validSize
    },
    styles () {
      let right = ''
      let bottom = ''
      let height = ''
      let width = ''

      switch (this.size) {
        case 'sm':
          right = '-11px'
          bottom = '-11px'
          height = '32px'
          width = '32px'
          break
        case 'md':
          right = '-11px'
          bottom = '-11px'
          height = '36px'
          width = '36px'
          break
        case 'lg':
          right = '-8px'
          bottom = '-8px'
          height = '36px'
          width = '36px'
          break
        case 'xl':
          right = '-11px'
          bottom = '-11px'
          height = '48px'
          width = '48px'
          break
        case 'xxl':
          right = '0'
          bottom = '-4px'
          height = '48px'
          width = '48px'
          break
        case 'xxxl':
          right = '12px'
          bottom = '4px'
          height = '52px'
          width = '52px'
          break
        default:
          right = '-11px'
          bottom = '-11px'
          height = '32px'
          width = '32px'
      }
      return {
        right,
        bottom,
        height,
        width
      }
    },
    getCameraSize () {
      switch (this.size) {
        case 'sm':
          return '20'
        case 'md':
          return '24'
        case 'lg':
          return '24'
        case 'xl':
          return '36'
        case 'xxl':
          return '36'
        case 'xxxl':
          return '40'
        default:
          return '20'
      }
    }
  },
  methods: {
    handleCameraClick () {
      this.$emit('upload-photo')
    },
    handleImageError () {
      this.imageError = true
    },
    calcPosition (offset) {
      return offset > 0
        ? `calc(100% - ${offset}px)`
        : `calc(100% + ${offset * -1}px)`
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui/index.scss';

.avatar-overlay {
  position: absolute;
  width: 100%;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 1;
}
.overlay-icon {
  vertical-align: top;
}
.initials-overlay {
  position: absolute;
  width: 100%;
  bottom: 0;
  z-index: 1;

  .v-icon {
    color: $sn-brand-03;
  }
}
.item {
  position:relative;
  display:inline-block;
}
.notify-badge{
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute !important;
  background: $sn-brand-03;
  text-align: center;
  border-radius: 50%;
  color: #FFFFFF !important;
  padding: 4px;
  border: 2px solid $sn-white;
  opacity: .8;
}

.icon-color {
  color: $sn-white !important;
}

/** This font should only ever be used in SnAvatar so Vlad (the UIGuy) indicated this should not be added to our SNUI typography */
.xxxs-font {
  font-family: Open Sans;
  font-size: 8px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
}
</style>
